import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    ArrowRightLeft,
    Key,
    AlertTriangle,
    PhoneOff,
    Clock,
    MessageCircle,
    Send,
    Trash2,
    Phone,
    PhoneCall,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Shield,
    Lock
} from "lucide-react";
import { toast } from "sonner";
import {
    createLead,
    createLeadComment,
    deleteLeadComment,
    getLeadDataById,
    updateLead,
    fetchDistinctValues
} from "../services/crmServices/leadService";
import { fetchAvailableDesks } from "../services/crmServices/deskService";
import { fetchUsers, searchUsers } from "../services/crmServices/userService";
import { getFullName } from "../services/common/util";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { selectFilteredLeads, selectCurrentLeadIndex, setCurrentLeadIndex } from "../store/slices/leadsSlice";
import { useAuth } from "../hooks/useAuth";
import { UpdatePasswordModal } from "../components/leads/UpdatePasswordModal";
import { AgentTransferModal } from "../components/transfers/AgentTransferModal";
import { VoiceCallModal } from "../components/leads/VoiceCallModal";

import { LeadHistory } from "../components/leads/LeadHistory";
import moment from "moment-timezone";
import { Country } from "country-state-city";
import { searchPositions } from "../services/crmServices/positionService";
import {
    initiateVoisoCall,
    initiateCommpeakCall,
    initiateCoperatoCall,
    initiateSquareTalkCall
} from "../services/voipServices";
import { createLog } from "../services/crmServices/logService";
import { format } from "date-fns";

export function CreateLead() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const filteredLeads = useSelector(selectFilteredLeads);
    const currentLeadIndex = useSelector(selectCurrentLeadIndex);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const leadId = params._id;

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        secondPhone: "",
        status: "new",
        desk: null,
        agent: null,
        floorManager: null,
        source: "",
        affiliate: "",
        country: "",
        countryCode: "", // Add this back
        city: "",
        address: "",
        lastComment: "",
        extra: "",
        internalNotes: ""
    });

    const [showAgentTransferModal, setShowAgentTransferModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showCallModal, setShowCallModal] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [accessDenied, setAccessDenied] = useState(false);
    const [loadingLead, setLoadingLead] = useState(false);
    const [localTime, setLocalTime] = useState({
        time: "",
        location: "Your Local Time",
        timezone: ""
    });

    const countries = Country.getAllCountries().map((country) => ({
        label: country.name,
        value: country.name,
        code: country.isoCode
    }));

    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState("");
    const [callLoading, setCallLoading] = useState(false);
    const [floorManagers, setFloorManagers] = useState([]);
    const [isCustomSource, setIsCustomSource] = useState(false);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);

    // Fetch lead data if editing
    useEffect(() => {
        if (!user.permissions.leads.update && leadId) {
            navigate("/leads");
        }
        initLead();

        // Find and set the current lead index in filtered leads if editing
        if (leadId && filteredLeads.length > 0) {
            const leadIndex = filteredLeads.findIndex((lead) => lead._id === leadId);
            if (leadIndex !== -1) {
                dispatch(setCurrentLeadIndex(leadIndex));
            }
        }
    }, [leadId, filteredLeads]);

    const initLead = () => {
        if (leadId) {
            setLoadingLead(true);
            setAccessDenied(false);
            getLeadDataById(leadId).then((res) => {
                if (res.data?._id) {
                    const lead = res.data;
                    setCurrentLead(lead);
                    setLoadingLead(false);
                    setFormData((prev) => ({
                        ...prev,
                        firstName: lead.firstName || "",
                        lastName: lead.lastName || "",
                        email: lead.email || "",
                        phone: lead.phone || "",
                        secondPhone: lead.secondPhone || "",
                        status: lead.status || "new",
                        desk: lead.desk?._id || null,
                        agent: lead.agent?._id || null,
                        floorManager:
                            (lead.floorManager && (lead.floorManager._id || lead.floorManager)) ||
                            (lead.desk?.floorManager && (lead.desk.floorManager._id || lead.desk.floorManager)) ||
                            null,
                        source: lead.source || "",
                        affiliate: lead.affiliate || "",
                        country: lead.country || "",
                        countryCode: lead.country
                            ? countries.find((country) => country.value === lead.country)?.code
                            : null,
                        city: lead.city || "",
                        address: lead.address || "",
                        lastComment: lead.lastComment || "",
                        extra: lead.extra || "",
                        internalNotes: lead.internalNotes || ""
                    }));
                    if (lead.comments && lead.comments.length) {
                        // Map comments to ensure they have id field (from _id) and proper structure
                        // Sort by createdAt descending (newest first)
                        const mappedComments = lead.comments
                            .map(comment => ({
                                ...comment,
                                id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                                isSystem: comment.isSystem || false
                            }))
                            .sort((a, b) => {
                                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                return dateB - dateA; // Descending order (newest first)
                            });
                        setComments(mappedComments);
                    } else {
                        setComments([]);
                    }

                    // if (lead.desk) {

                    // }
                    fetchUsers().then((res) => {
                        if (res?.data?.length) {
                            if (lead.desk) {
                                const filteredAgents = res.data.filter((user) => user.agentDesk?._id === lead.desk._id);
                                setAgents(filteredAgents);
                            } else if (lead.agent) {
                                const filteredAgents = res.data.filter((user) => user._id === lead.agent._id);
                                setAgents(filteredAgents);
                            }
                        } else {
                            setAgents([]);
                        }
                    });
                } else {
                    setLoadingLead(false);
                }
            }).catch((error) => {
                // Handle network errors or other errors
                setLoadingLead(false);
                if (error.response?.status === 403) {
                    // Access denied - lead is no longer assigned to this user
                    setAccessDenied(true);
                    const errorMessage = error.response?.data?.message || error.response?.data?.data?.message || "You no longer have access to this lead. It may have been reassigned.";
                    toast.error(errorMessage);
                } else if (error.response?.status === 404) {
                    setAccessDenied(true);
                    toast.error("Lead not found");
                } else {
                    // For other errors, still show access denied if we're trying to edit a lead
                    if (leadId) {
                        setAccessDenied(true);
                    }
                    toast.error(error.response?.data?.message || "Failed to load lead data");
                }
            });
        }
        fetchAvailableDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        fetchUsers().then((res) => {
            setUsers(res.data);
        });
    };

    // Load floor managers for assignment dropdown
    useEffect(() => {
        const loadFloorManagers = async () => {
            try {
                const positionsRes = await searchPositions({ role: "floorManager" });
                const positions = positionsRes?.data || [];
                if (!positions.length) {
                    setFloorManagers([]);
                    return;
                }
                const positionIds = positions.map((pos) => pos._id);
                const usersRes = await searchUsers({ position: { $in: positionIds } });
                setFloorManagers(usersRes?.data || []);
            } catch (error) {
                console.error("Error loading floor managers:", error);
                setFloorManagers([]);
            }
        };

        loadFloorManagers();
    }, []);

    // Load distinct sources for source dropdown
    useEffect(() => {
        const loadSources = async () => {
            try {
                const res = await fetchDistinctValues("source");
                if (res?.data) {
                    setSourceOptions(
                        res.data.map((source) => ({
                            label: source,
                            value: source
                        }))
                    );
                }
            } catch (error) {
                console.error("Error fetching sources for create lead:", error);
            }
        };

        loadSources();
    }, []);

    // Enhanced timezone lookup - return string instead of object
    const getTimezoneByCountry = (countryName) => {
        const selectedCountry = countries.find((c) => c.value === countryName);
        return selectedCountry?.timezone?.zoneName || "UTC";
        // return "asdf";
    };

    // Get timezone for specific city using coordinates
    const getTimezoneByCity = (countryCode, cityName) => {
        if (!countryCode || !cityName) return getTimezoneByCountry(formData.country);

        // Check if the city name matches any timezone
        const cityTimezone = moment.tz
            .names()
            .find((tz) => tz.toLowerCase().includes(cityName.toLowerCase().replace(/\s+/g, "_")));

        if (cityTimezone) {
            return cityTimezone;
        }

        return getTimezoneByCountry(formData.country);
    };

    // Update local time - show user's local time by default
    useEffect(() => {
        const updateLocalTime = () => {
            let timezone;
            let locationLabel;

            if (formData.country) {
                // Use lead's location if country is selected
                if (formData.city && formData.countryCode) {
                    timezone = getTimezoneByCity(formData.countryCode, formData.city);
                    locationLabel = `${formData.city}, ${formData.country}`;
                } else {
                    timezone = getTimezoneByCountry(formData.country);
                    locationLabel = formData.country;
                }
            } else {
                // Use user's local timezone as default
                timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                locationLabel = "Your Local Time";
            }

            const now = new Date();
            const localDateTime = new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                weekday: "short"
            }).format(now);

            // setLocalTime(localDateTime);

            // Store location label for display
            setLocalTime(() => {
                const timeData = {
                    time: localDateTime,
                    location: locationLabel,
                    timezone: timezone
                };
                return timeData;
            });
        };

        updateLocalTime();
        const interval = setInterval(updateLocalTime, 1000);
        return () => clearInterval(interval);
    }, [formData.country, formData.city, formData.countryCode]);

    const handleAddComment = async () => {
        if (newComment.trim()) {
            const comment = {
                leadId: leadId,
                text: newComment.trim(),
                author: user._id,
                createdAt: moment().tz("America/New_York").toDate()
            };

            try {
                const response = await createLeadComment(comment);
                if (response?.status === 200) {
                    setNewComment("");
                    toast.success("Comment added successfully");
                    
                    // Refresh lead data to get populated comment author (same as delete)
                    if (leadId) {
                        const res = await getLeadDataById(leadId);
                        if (res.data?._id) {
                            setCurrentLead(res.data);
                            if (res.data.comments && res.data.comments.length) {
                                const mappedComments = res.data.comments
                                    .map(comment => ({
                                        ...comment,
                                        id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                                        isSystem: comment.isSystem || false
                                    }))
                                    .sort((a, b) => {
                                        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                        return dateB - dateA; // Descending order (newest first)
                                    });
                                setComments(mappedComments);
                            } else {
                                setComments([]);
                            }
                        }
                    }
                } else if (response.data?.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.error(response.message || "Failed to add comment");
                }
            } catch (error) {
                console.error("Error adding comment:", error);
                toast.error(error.response?.data?.message || error.message || "Failed to add comment. Please try again.");
            }
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        try {
            const response = await deleteLeadComment({
                leadId: leadId,
                commentId: commentId
            });

            if (response?.status === 200) {
                // Remove comment from local state
                setComments((prev) => prev.filter((comment) => {
                    const id = comment._id || comment.id;
                    return id !== commentId;
                }));
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated comments
                if (leadId) {
                    const res = await getLeadDataById(leadId);
                    if (res.data?._id) {
                        setCurrentLead(res.data);
                        if (res.data.comments && res.data.comments.length) {
                            const mappedComments = res.data.comments
                                .map(comment => ({
                                    ...comment,
                                    id: comment._id || comment.id || Math.random().toString(36).substr(2, 9),
                                    isSystem: comment.isSystem || false
                                }))
                                .sort((a, b) => {
                                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                    return dateB - dateA; // Descending order (newest first)
                                });
                            setComments(mappedComments);
                        } else {
                            setComments([]);
                        }
                    }
                }
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleDeleteLastComment = async () => {
        if (!window.confirm("Are you sure you want to delete this comment?")) {
            return;
        }

        if (!leadId) {
            toast.error("Cannot delete comment: Lead ID is missing");
            return;
        }

        try {
            const response = await updateLead({ 
                _id: leadId, 
                lastComment: "" 
            });

            if (response?.status === 200) {
                toast.success("Comment deleted successfully");
                
                // Refresh lead data to get updated lastComment
                const res = await getLeadDataById(leadId);
                if (res.data?._id) {
                    setCurrentLead(res.data);
                }
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting last comment:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to delete comment. Please try again.");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAddComment();
        }
    };

    // Add this validation function
    const validatePhoneNumbers = () => {
        const phone = formData.phone?.trim();
        const secondPhone = formData.secondPhone?.trim();

        // If both phones are provided and they are the same
        if (phone && secondPhone && phone === secondPhone) {
            return {
                isValid: false,
                message: "Phone number and Second Phone cannot be the same"
            };
        }

        return { isValid: true };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate that either firstName or lastName is provided
        const hasFirstName = formData.firstName && formData.firstName.trim() !== "";
        const hasLastName = formData.lastName && formData.lastName.trim() !== "";
        if (!hasFirstName && !hasLastName) {
            toast.error("First Name or Last Name is required");
            return;
        }

        // Validate phone numbers
        const phoneValidation = validatePhoneNumbers();
        if (!phoneValidation.isValid) {
            toast.error(phoneValidation.message);
            return;
        }

        if (leadId) {
            updateLead({ _id: leadId, ...formData }).then((response) => {
                if (response?.status === 200) {
                    toast.success("Lead updated successfully!");
                    navigate("/leads");
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to update lead");
                    }
                }
            });
        } else {
            // When creating a new lead, copy extra field content to lastComment and generate default password
            // Generate a default password (email + timestamp if email exists, otherwise random string)
            const defaultPassword = formData.email 
                ? `${formData.email}_${Date.now()}` 
                : `lead_${Date.now()}_${Math.random().toString(36).substring(7)}`;
            
            const leadDataToCreate = {
                ...formData,
                password: defaultPassword,
                lastComment: formData.extra ? formData.extra : formData.lastComment || ""
            };
            
            console.log("[CREATE LEAD FRONTEND] Submitting lead with assignment:", {
                floorManager: leadDataToCreate.floorManager,
                desk: leadDataToCreate.desk,
                agent: leadDataToCreate.agent
            });
            
            createLead(leadDataToCreate).then((response) => {
                if (response?.status === 200) {
                    toast.success("Lead created successfully!");
                    navigate("/leads");
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to create lead");
                    }
                }
            });
        }
    };

    const handleDeskChange = (value) => {
        if (value !== formData.desk) {
            console.log("[DESK CHANGE] Selected desk:", value);
            // Immediately clear agents and show loading state for better UX
            setAgents([]);
            setAgentsLoading(true);
            fetchUsers().then((res) => {
                if (res?.data?.length) {
                    const filteredAgents = res.data.filter((user) => user.agentDesk?._id === value);
                    console.log("[DESK CHANGE] Found agents in desk:", filteredAgents.length);
                    setAgents(filteredAgents);
                    setAgentsLoading(false);
                } else {
                    console.log("[DESK CHANGE] No agents found");
                    setAgents([]);
                    setAgentsLoading(false);
                }
            }).catch((error) => {
                console.error("[DESK CHANGE] Error fetching agents:", error);
                setAgents([]);
                setAgentsLoading(false);
            });
            setFormData((prev) => ({ ...prev, desk: value, agent: null }));
        }
    };

    const handleAgentTransferSuccess = () => {
        setShowAgentTransferModal(false);
        toast.success("Transfer request submitted successfully! Redirecting to leads page...");
        setTimeout(() => {
            navigate("/leads");
        }, 1500);
    };
    const handlePasswordUpdateSuccess = () => {
        setShowPasswordModal(false);
        toast.success("Password updated successfully!");
    };

    const handleBack = () => {
        navigate("/leads");
    };

    const handlePrevious = () => {
        if (currentLeadIndex > 0) {
            const previousLead = filteredLeads[currentLeadIndex - 1];
            navigate(`/leads/edit/${previousLead._id}`);
        }
    };

    const handleNext = () => {
        if (currentLeadIndex < filteredLeads.length - 1) {
            const nextLead = filteredLeads[currentLeadIndex + 1];
            navigate(`/leads/edit/${nextLead._id}`);
        }
    };

    const handleCountryChange = (value) => {
        const selectedCountry = countries.find((country) => country.value === value);
        setFormData((prev) => ({
            ...prev,
            country: value,
            countryCode: selectedCountry?.code || "",
            city: "" // Reset city when country changes
        }));
    };

    const handleCityChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, city: value }));
    };

    // Check if phone number is missing
    const isPhoneMissing =
        (!formData.phone || formData.phone.trim() === "") &&
        (!formData.secondPhone || formData.secondPhone.trim() === "");

    // Add real-time validation with visual feedback
    const [phoneError, setPhoneError] = useState("");

    // Desks filtered by selected floor manager (if any)
    const filteredDesks = formData.floorManager
        ? desks.filter((desk) => {
              const fmId = desk.floorManager?._id || desk.floorManager;
              if (!fmId) return false;
              const fmIdStr = fmId.toString ? fmId.toString() : String(fmId);
              const selectedIdStr = formData.floorManager.toString
                  ? formData.floorManager.toString()
                  : String(formData.floorManager);
              return fmIdStr === selectedIdStr;
          })
        : desks;

    const canEditSource =
        user.position?.role === "admin" || user.permissions?.editLeadFields?.source;

    // If existing source value is not in dropdown options (e.g., old custom value),
    // default to custom mode so it can be edited as free text.
    useEffect(() => {
        if (formData.source && sourceOptions.length > 0) {
            const exists = sourceOptions.some((opt) => opt.value === formData.source);
            if (!exists) {
                setIsCustomSource(true);
            }
        }
    }, [formData.source, sourceOptions]);

    // Add useEffect to validate phones when they change
    useEffect(() => {
        const phone = formData.phone?.trim();
        const secondPhone = formData.secondPhone?.trim();

        if (phone && secondPhone && phone === secondPhone) {
            setPhoneError("Phone numbers cannot be the same");
        } else {
            setPhoneError("");
        }
    }, [formData.phone, formData.secondPhone]);

    // Show access denied message if user doesn't have access
    if (accessDenied && leadId) {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-8">
                <Card className="max-w-2xl mx-auto">
                    <CardContent className="p-8 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="bg-red-100 rounded-full p-4">
                                <Lock className="h-12 w-12 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
                            <p className="text-gray-600 max-w-md">
                                You no longer have access to this lead. It may have been reassigned to another agent or removed from your access.
                            </p>
                            <div className="flex space-x-4 mt-6">
                                <Button onClick={() => navigate("/leads")}>
                                    Go to Leads
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show loading state while checking access
    if (loadingLead && leadId) {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                        <p className="text-gray-600">Loading lead...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <h1 className="text-2xl font-bold text-gray-900">{leadId ? "Edit" : "Create"} Lead</h1>
                    </div>

                    {/* Action buttons for editing */}
                    {leadId && currentLead && (
                        <div className="flex space-x-3">
                            {user.position.role === "agent" && user.position.floor === "conversion" && (
                                <Button
                                    onClick={() => setShowAgentTransferModal(true)}
                                    variant="outline"
                                    className="border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
                                >
                                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                                    Transfer Lead
                                </Button>
                            )}
                            {user.position.role === "admin" && (
                                <Button
                                    onClick={() => setShowPasswordModal(true)}
                                    variant="outline"
                                    className="border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                                >
                                    <Key className="h-4 w-4 mr-2" />
                                    Update Password
                                </Button>
                            )}
                            {/* Call Button - Only show for existing leads with phone numbers */}
                            {leadId && currentLead && (currentLead.phone || currentLead.secondPhone) && (
                                <Button
                                    onClick={() => setShowCallModal(true)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                    <Phone className="h-4 w-4 mr-2" />
                                    Call Lead
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigation controls - Only show in edit mode */}
                {leadId && filteredLeads.length > 1 && (
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mt-4">
                        <Button
                            variant="outline"
                            onClick={handlePrevious}
                            disabled={currentLeadIndex <= 0}
                            className="flex items-center transition-colors"
                        >
                            <ChevronLeft className="h-4 w-4 mr-2" />
                            Previous
                        </Button>

                        <span className="text-sm text-gray-600">
                            {currentLeadIndex + 1} of {filteredLeads.length} leads
                        </span>

                        <Button
                            variant="outline"
                            onClick={handleNext}
                            disabled={currentLeadIndex >= filteredLeads.length - 1}
                            className="flex items-center transition-colors"
                        >
                            Next
                            <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form - Left side (2/3 width) */}
                <div className="lg:col-span-2">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Basic Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="firstName">First Name <span className="text-gray-500 text-sm">(or Last Name required)</span></Label>
                                        <Input
                                            id="firstName"
                                            value={formData.firstName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.firstName ? false : true}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="lastName">Last Name <span className="text-gray-500 text-sm">(or First Name required)</span></Label>
                                        <Input
                                            id="lastName"
                                            value={formData.lastName}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.lastName ? false : true}
                                        />
                                    </div>
                                </div>
                                {user.permissions.leads.viewEmail && (
                                    <div>
                                        <Label htmlFor="email">Email Address *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, email: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.email ? false : true}
                                            required
                                        />
                                    </div>
                                )}
                                {user.permissions.leads.viewPhone && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="phone">Phone Number</Label>
                                            <Input
                                                id="phone"
                                                value={formData.phone}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                                                }
                                                disabled={user.permissions.editLeadFields?.phone ? false : true}
                                                className={phoneError ? "border-red-500" : ""}
                                            />
                                            {/* Phone number warning */}
                                            {isPhoneMissing && !phoneError && (
                                                <div className="mt-2 flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                    <PhoneOff className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                    <div className="text-sm">
                                                        <p className="font-medium text-yellow-800">
                                                            Call functionality not available
                                                        </p>
                                                        <p className="text-yellow-700">
                                                            No phone number listed. Voice calling will be disabled for
                                                            this lead.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <Label htmlFor="secondPhone">Second Phone</Label>
                                            <Input
                                                id="secondPhone"
                                                value={formData.secondPhone}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, secondPhone: e.target.value }))
                                                }
                                                disabled={user.permissions.editLeadFields?.secondPhone ? false : true}
                                                className={phoneError ? "border-red-500" : ""}
                                            />
                                        </div>
                                        {/* Show validation error */}
                                        {phoneError && (
                                            <div className="col-span-1 md:col-span-2 mt-2 flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                <div className="text-sm">
                                                    <p className="font-medium text-red-800">Validation Error</p>
                                                    <p className="text-red-700">{phoneError}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Location & Status</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="country">Country</Label>
                                        <Select
                                            options={countries}
                                            value={formData.country}
                                            onChange={handleCountryChange}
                                            placeholder="Select country"
                                            searchable
                                            disabled={user.permissions.editLeadFields?.country ? false : true}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="city">City</Label>
                                        <Input
                                            id="city"
                                            value={formData.city}
                                            onChange={handleCityChange}
                                            placeholder={formData.country ? "Enter city name" : "Select country first"}
                                            disabled={
                                                !formData.country ||
                                                (user.permissions.editLeadFields?.city ? false : true)
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="address">Address</Label>
                                        <Input
                                            id="address"
                                            value={formData.address}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, address: e.target.value }))
                                            }
                                            disabled={user.permissions.editLeadFields?.address ? false : true}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="status">Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={formData.status}
                                            onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                                            placeholder="Select status"
                                            searchable
                                            disabled={user.permissions.editLeadFields?.status ? false : true}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Assignment & Source</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Show desk/agent fields only for new leads */}
                                {(!leadId || user.position.role !== "agent") && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <Label htmlFor="floorManager">Floor Manager</Label>
                                                {formData.floorManager && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                                        onClick={() => {
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                floorManager: null,
                                                                desk: null,
                                                                agent: null
                                                            }));
                                                            setAgents([]);
                                                        }}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </div>
                                            <Select
                                                options={floorManagers.map((fm) => ({
                                                    label: getFullName(fm),
                                                    value: fm._id
                                                }))}
                                                value={formData.floorManager}
                                                onChange={(value) => {
                                                    console.log("[FLOOR MANAGER CHANGE] Selected:", value);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        floorManager: value,
                                                        desk: null,
                                                        agent: null
                                                    }));
                                                    setAgents([]);
                                                }}
                                                placeholder="Select floor manager"
                                                searchable
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="desk">Desk</Label>
                                            <Select
                                                options={filteredDesks.map((desk) => ({
                                                    label: desk.name,
                                                    value: desk._id
                                                }))}
                                                value={formData.desk}
                                                onChange={(value) => handleDeskChange(value)}
                                                placeholder="Select desk"
                                                searchable
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="agent">Agent</Label>
                                            <Select
                                                options={agents.map((agent) => ({
                                                    label: getFullName(agent),
                                                    value: agent._id
                                                }))}
                                                value={formData.agent}
                                                onChange={(value) => {
                                                    console.log("[AGENT CHANGE] Selected:", value);
                                                    setFormData((prev) => ({ ...prev, agent: value }));
                                                }}
                                                placeholder={agentsLoading ? "Loading agents..." : "Select agent"}
                                                searchable
                                                disabled={agentsLoading || !formData.desk}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Show current assignment for editing leads */}
                                {leadId && currentLead && user.position.role === "agent" && (
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-medium mb-3">Current Assignment</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-gray-600">Desk:</span>
                                                <span className="ml-2 font-medium">
                                                    {currentLead.desk?.name || "Not assigned"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Agent:</span>
                                                <span className="ml-2 font-medium">
                                                    {currentLead.agent
                                                        ? getFullName(currentLead.agent)
                                                        : "Not assigned"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Manager:</span>
                                                <span className="ml-2 font-medium">
                                                    {currentLead.desk?.manager
                                                        ? getFullName(currentLead.desk.manager)
                                                        : "Not assigned"}
                                                </span>
                                            </div>
                                        </div>
                                        {user.position.role === "admin" && (
                                            <p className="text-xs text-blue-600 mt-2">
                                                Use the &quot;Transfer Lead&quot; button above to change desk or agent
                                                assignment.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="source">Source</Label>
                                        {canEditSource && !isCustomSource && (
                                            <Select
                                                options={[
                                                    ...sourceOptions,
                                                    { label: "Add new source...", value: "__custom__" }
                                                ]}
                                                value={
                                                    sourceOptions.some(
                                                        (opt) => opt.value === formData.source
                                                    )
                                                        ? formData.source
                                                        : ""
                                                }
                                                onChange={(value) => {
                                                    if (value === "__custom__") {
                                                        setIsCustomSource(true);
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: ""
                                                        }));
                                                    } else {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: value
                                                        }));
                                                    }
                                                }}
                                                placeholder="Select source"
                                                searchable
                                                disabled={!canEditSource}
                                            />
                                        )}
                                        {(!canEditSource || isCustomSource) && (
                                            <div className="space-y-1">
                                                <Input
                                                    id="source"
                                                    value={formData.source}
                                                    onChange={(e) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            source: e.target.value
                                                        }))
                                                    }
                                                    disabled={!canEditSource}
                                                    placeholder="Enter source"
                                                />
                                                {canEditSource && sourceOptions.length > 0 && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 text-xs text-gray-500 hover:text-gray-700 mt-1"
                                                        onClick={() => setIsCustomSource(false)}
                                                    >
                                                        Use existing source list
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label htmlFor="affiliate">Affiliate</Label>
                                        <Input
                                            id="affiliate"
                                            value={formData.affiliate}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, affiliate: e.target.value }))
                                            }
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {user.position.role === "admin" && (
                            <Card className="border-amber-200 bg-amber-50/30">
                            <CardHeader>
                                    <CardTitle className="flex items-center text-amber-800">
                                        <Shield className="h-5 w-5 mr-2" />
                                        Internal Notes
                                        <Badge variant="outline" className="ml-2 text-xs border-amber-300 text-amber-700">Admin Only</Badge>
                                    </CardTitle>
                            </CardHeader>
                            <CardContent>
                                    <Label htmlFor="internalNotes">Private notes (visible only to administrators)</Label>
                                <Textarea
                                        id="internalNotes"
                                        value={formData.internalNotes}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, internalNotes: e.target.value }))}
                                        rows={3}
                                        placeholder="Add private internal notes..."
                                        className="mt-1"
                                />
                            </CardContent>
                        </Card>
                        )}

                        <div className="flex justify-end space-x-4">
                            <Button type="submit">
                                {leadId ? "Update" : "Create"} Lead
                            </Button>
                            <Button type="button" variant="outline" onClick={handleBack}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Right Sidebar - Enhanced Local Time & Comments */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Enhanced Local Time Card - Always show */}
                    <Card className="border-gray-200 shadow-sm">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center text-lg">
                                <div className="p-2 bg-indigo-600 rounded-full mr-3">
                                    <Clock className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <span className="text-gray-900">Local Time</span>
                                    <p className="text-xs text-gray-600 font-normal mt-1">
                                        {localTime.location || "Your Local Time"}
                                    </p>
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-2">
                            <div className="text-center bg-gray-50 rounded-lg p-4 border border-gray-100">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                                    <span className="text-xs text-gray-500 uppercase tracking-wide">Live Time</span>
                                </div>
                                <div className="text-xl font-mono font-bold text-gray-900 mb-1">
                                    {typeof localTime === "object" ? localTime.time : localTime}
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                        {localTime.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Comments Card - Only show for existing leads */}
                    {leadId && (
                        <Card className="h-[600px] flex flex-col overflow-hidden">
                            <CardHeader className="pb-3 flex-shrink-0">
                                <CardTitle className="flex items-center text-lg">
                                    <MessageCircle className="h-5 w-5 mr-2 text-green-600" />
                                    Comments ({comments.length + (currentLead?.lastComment ? 1 : 0)})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden">
                                {/* Add Comment Section */}
                                <div className="p-4 border-b flex-shrink-0">
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Add a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            rows={2}
                                            className="flex-1 resize-none"
                                        />
                                        <Button
                                            onClick={handleAddComment}
                                            disabled={!newComment.trim()}
                                            size="sm"
                                            className="self-end transition-colors"
                                        >
                                            <Send className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Press Enter to send, Shift+Enter for new line
                                    </p>
                                </div>

                                {/* Comments List */}
                                <div className="flex-1 overflow-y-auto p-4 min-h-0">
                                    {comments.length === 0 && !currentLead?.lastComment ? (
                                        <div className="text-center text-gray-500 py-8">
                                            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p>No comments yet</p>
                                            <p className="text-sm">Add the first comment above</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Display lastComment if it exists */}
                                            {currentLead?.lastComment && (
                                                <div className="p-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-400 break-words">
                                                    <div className="flex justify-between items-start mb-2 gap-2">
                                                        <span className="text-sm font-medium text-yellow-800">
                                                            Last Comment
                                                        </span>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                {currentLead.updatedAt 
                                                                    ? format(new Date(currentLead.updatedAt), "MMM dd, HH:mm")
                                                                    : ""}
                                                            </span>
                                                            {user.position.role === "admin" && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={handleDeleteLastComment}
                                                                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    title="Delete comment"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere">
                                                        {currentLead.lastComment}
                                                    </p>
                                                </div>
                                            )}
                                            {comments.map((comment) => {
                                                const commentId = comment._id || comment.id;
                                                return (
                                                    <div
                                                        key={comment.id}
                                                        className={`p-3 rounded-lg break-words ${
                                                            comment.isSystem
                                                                ? "bg-blue-50 border-l-4 border-blue-400"
                                                                : "bg-gray-50"
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start mb-2 gap-2">
                                                            <span
                                                                className={`text-sm font-medium break-words ${
                                                                    comment.isSystem ? "text-blue-700" : "text-gray-900"
                                                                }`}
                                                            >
                                                                {comment.affiliateName 
                                                                    ? `Comment by ${comment.affiliateName}`
                                                                    : comment.author
                                                                        ? getFullName(comment.author)
                                                                        : "Unknown User"}
                                                            </span>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                                                    {format(new Date(comment.createdAt), "MMM dd, HH:mm")}
                                                                </span>
                                                                {user.position.role === "admin" && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteComment(commentId)}
                                                                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        title="Delete comment"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere">
                                                            {comment.text}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            {/* Lead History Section - Only show for existing leads */}
            {leadId && (
                <div className="mt-6">
                    <LeadHistory leadId={leadId} />
                </div>
            )}

            {/* Agent Transfer Modal */}
            <AgentTransferModal
                open={showAgentTransferModal}
                onClose={() => setShowAgentTransferModal(false)}
                onSuccess={handleAgentTransferSuccess}
                lead={currentLead}
            />

            {/* Update Password Modal */}
            <UpdatePasswordModal
                open={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordUpdateSuccess}
                lead={currentLead}
            />

            {/* Voice Call Modal */}
            <VoiceCallModal
                open={showCallModal}
                onClose={() => {
                    setShowCallModal(false);
                    initLead(); // Refresh lead data after call
                }}
                lead={currentLead}
                onLeadUpdate={(updatedLead) => {
                    // Don't update currentLead here as it causes re-render that closes/reopens modal
                    // The modal will handle the update internally via localLead
                    // We'll refresh currentLead when modal closes via onClose callback
                }}
                onSuccess={() => {
                    setShowCallModal(false);
                    // Refresh lead data after call
                    if (leadId) {
                        getLeadDataById(leadId).then((res) => {
                            if (res.data?._id) {
                                setCurrentLead(res.data);
                                if (res.data.comments && res.data.comments.length) {
                                    setComments(res.data.comments);
                                }
                            }
                        });
                    }
                }}
            />
        </div>
    );
}
